"use client";
import useFonts from "@/lib/hooks/useFonts";
import { cn } from "@/lib/helpers/utils";
import {
  QuranWbwIndoPak15ResponseType,
  QuranWbwIndoPak16ResponseType,
  QuranWbwUthmaniResponseType,
} from "@/lib/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuranOptions } from "@/app/providers/QuranContextProvider";
import { indoPak, uthmaniHafs } from "@/app/fonts";
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import QuranWbwTranslations from "./quran-wbw-translation";
import QuranMorphologyView from "./view-morphology";
import QuranSameWordsView from "./view-same-words";
import QuranRootWordsView from "./view-root-words";
import { Volume2Icon } from "lucide-react";
import { useDebounce } from "@/lib/hooks/useDebounce";

export const QuranWord: React.FC<{
  preview?: boolean;
  word:
    | QuranWbwUthmaniResponseType
    | QuranWbwIndoPak15ResponseType
    | QuranWbwIndoPak16ResponseType;
}> = ({ word, preview = true }) => {
  const { quranFont, style, quranMushaf, quranFontSize } = useQuranOptions();
  const { isFontLoaded } = useFonts(`${quranFont}-p${word.page}`);

  const wrd = useMemo(() => {
    let text: string = "";
    let codeText: string = "";
    let className = uthmaniHafs.className;

    if (isFontLoaded) {
      let wrd = word as QuranWbwUthmaniResponseType;
      if (quranFont == "hafs1") codeText = wrd.code_v1;
      else codeText = wrd.code_v2;
      text = wrd.uthmani_hafs;
      className = "";
    } else {
      if (quranMushaf.type == "hafs1" || quranMushaf.type == "hafs2") {
        if ("text_uthmani" in word) {
          if (quranFont == "hafs1") codeText = word.code_v1;
          else codeText = word.code_v2;
          text = word.uthmani_hafs;
        }
      } else {
        if ("words_text" in word) {
          text = word.words_text!!;
          codeText = word.words_text!!;
          className = indoPak.className;
        }
      }
    }

    const wrd = isFontLoaded ? codeText : text;
    return (
      <span
        // key={quranMushaf.type}
        id={`${word.surah}:${word.verse}:${word.word}:${word.id}`}
        className={cn(
          `text-text hover:text-primary/40 z-10`,
          style == "translation" ? " pr-2" : "",

          //Todo remove this....... Modify the code so the font is not changed unless loaded. Keep the previous font
          // clsName.current,
          // className,
          isFontLoaded
            ? ""
            : quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16"
              ? indoPak.className
              : uthmaniHafs.className,
          word.type == "end" ? " text-primary" : "",
        )}
        style={{
          fontFamily: isFontLoaded ? `${quranFont}-p${word.page}` : "",
        }}
      >
        {word.type == "end" ? (
          <span>{wrd}</span>
        ) : (
          <WordDropDown word={word} text={wrd} isPreview={preview} />
        )}
      </span>
    );
  }, [isFontLoaded, quranMushaf, word, quranFont, style, preview]);

  return <>{wrd}</>;
};

const WordDropDown = ({
  word,
  text,
  isPreview,
}: {
  text: string;
  isPreview: boolean;
  word:
    | QuranWbwUthmaniResponseType
    | QuranWbwIndoPak16ResponseType
    | QuranWbwIndoPak15ResponseType;
}) => {
  const {
    showTooltip,
    showWordTranslations,
    showWordTransliteration,
    quranFontSize,
  } = useQuranOptions();
  const [open, setOpen] = useState(false);
  const { debouncedFunction } = useDebounce({
    callback: () => setOpen((p) => !p),
    delay: 200,
  });
  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };
  return isPreview || !showTooltip ? (
    <span>{text}</span>
  ) : (
    <Popover>
      <PopoverTrigger asChild>
        <div>
          <span>{text}</span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="border-none p-2 w-[15rem] bg-secondary">
        <PopoverArrow
          width={14}
          height={9}
          className="absolute left-1/2 top-[5px] h-3 w-5 -translate-x-1/2 -translate-y-1/2 text-card"
          style={{
            fill: "hsl(var(--secondary))",
          }}
        />
        {(showWordTranslations || showWordTransliteration) && (
          <div className="w-full h-auto bg-primary/20 flex flex-col justify-center text-center rounded-md text-sm pb-3">
            {showWordTranslations && (
              <QuranWbwTranslations
                wordId={word.id as number}
                page={word.page}
              />
            )}
            {showWordTransliteration && (
              <div className="text-text">{word.transliteration}</div>
            )}
          </div>
        )}
        <div className="justify-around pt-2 text-sm">
          <div className="hover:bg-primary/20 rounded-md p-[.4rem] cursor-default flex flex-row">
            <span className="pr-2">
              <Volume2Icon className="h-5 w-5 text-primary" />
            </span>
            <span className="relative text-text">Pronounce</span>
          </div>
          <div className="hover:bg-primary/20 rounded-md p-[.4rem] cursor-default flex flex-row">
            <span className="pr-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-primary"
              >
                <polygon points="6 3 20 12 6 21 6 3"></polygon>
              </svg>
            </span>
            <span className="relative text-text">Start recitation</span>
          </div>
          <QuranMorphologyView morphology={word.morphology} word={word} />
          <QuranRootWordsView word={word} />
          <QuranSameWordsView word={word} morphology={word.morphology} />
        </div>
      </PopoverContent>
    </Popover>
  );
};

QuranWord.displayName = "QuranWord";
